'use client'

import React, { useEffect, useMemo } from 'react'

import { IAnnualProgram } from '@/_types'
import { buildItem, getAnnualProgramItemCategory2, pushEvent } from '@/_utils/analytics/datalayer'
import {
	useAPDetailGender,
	ZAPDetailHero,
	ZAPDetailHighlights,
	ZAPDetailProgramDetail,
	ZAPDetailProgramContent,
	ZAPDetailChildProgram
} from '@/app/[locale]/annual-programs/_components/ap-detail'
import {
	HIGHLIGHT_35_AMBULANCES,
	HIGHLIGHT_HOLISTIC_APPROACH,
	HIGHLIGHT_AVAILABLE_APPOINTMENTS,
	HIGHLIGHT_FREE_PARKING,
	HIGHLIGHT_YEAR_ROUND_CARE
} from '@/app/[locale]/annual-programs/_constants'
import { useAPDetailProgram } from '@/app/[locale]/annual-programs/_hooks'

import AMVDescription from './a-mv-description'

interface IProps {
	maleProgram: IAnnualProgram
	femaleProgram: IAnnualProgram
}

export default function AMVContentWrapper({ maleProgram, femaleProgram }: IProps) {
	const { gender, setGender } = useAPDetailGender()
	const currentProgram = useAPDetailProgram(maleProgram, femaleProgram)

	useEffect(() => {
		const item = buildItem(
			{
				id: currentProgram.id,
				name: currentProgram.name,
				type: 'annual-program',
				price: currentProgram.price,
				priceOld: currentProgram.priceOld
			},
			{ item_category2: getAnnualProgramItemCategory2({ primaryCategory: gender }) }
		)
		pushEvent('view_item', { currency: 'EUR', value: currentProgram.price, items: [item] })
	}, [currentProgram, gender])

	const highlights = useMemo(
		() => [
			HIGHLIGHT_35_AMBULANCES,
			HIGHLIGHT_HOLISTIC_APPROACH,
			HIGHLIGHT_AVAILABLE_APPOINTMENTS,
			HIGHLIGHT_FREE_PARKING,
			HIGHLIGHT_YEAR_ROUND_CARE
		],
		[]
	)

	return (
		<>
			<ZAPDetailHero
				programName='Medante Vital'
				description='<b>Starajte sa o svoje zdravie celoročne.</b> S ročným programom Medante Vital máte zvýhodnené ceny, nadštandardné služby a ďalšie benefity Polikliniky Medante.'
				femaleButtonText='Medante Vital pre ženu'
				maleButtonText='Medante Vital pre muža'
				image={{
					src: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/annual-programs/stastny-par-v-prirode.webp`,
					alt: 'Žena a muž v prírode',
					height: 382,
					width: 625
				}}
			/>

			<ZAPDetailHighlights highlights={highlights} />

			<AMVDescription />

			<ZAPDetailChildProgram
				primaryCategory={gender}
				annualProgram={currentProgram}
				title='Preventívna prehliadka<br /><b>Medante BASIC</b> v cene'
				text='<p>Súčasťou programu Medante Vital je preventívna prehliadka Medante BASIC
						vrátane komplexného zhodnotenia výsledkov zdravotného stavu všeobecným lekárom.</p>'
				footerText='Viac o preventívnej prehliadke nájdete na <a href="/preventivne-prehliadky">tomto odkaze</a>.'
				image={{
					src: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/annual-programs/zena-a-muz-sedia-na-kopci.webp`,
					alt: 'Žena a muž v prírode'
				}}
				buttons={[
					{
						text: 'Ženy',
						onClick: () => setGender('female'),
						scrollToId: 'kupit-program',
						isPrimary: true,
						color: 'cyclamen'
					},
					{
						text: 'Muži',
						onClick: () => setGender('male'),
						scrollToId: 'kupit-program',
						isPrimary: true,
						color: 'blue'
					}
				]}
			/>

			<ZAPDetailProgramDetail
				primaryCategory={gender}
				annualProgram={currentProgram}
				hasModules={true}
			>
				<ZAPDetailProgramContent program={currentProgram} />
			</ZAPDetailProgramDetail>
		</>
	)
}
